import { useMemo } from "react";
import useGetWarehousesRest from "@/hooks/react-query/Warehouses/useGetWarehousesRest";

/**
 * El nombre del local, para escribirlo en pantalla.
 *
 * Devuelve `undefined` —nunca `"-"` ni `""`— cuando todavía no se sabe: quien lo
 * consume decide su propio texto de reemplazo. `NoLocalStockDialog`, por
 * ejemplo, cae a `"acá"`, que es una frase; un guión en medio de "No hay stock
 * de X en -" es peor que no nombrar el local.
 *
 * Comparte la query key `["warehouse-names"]` con `useOtherWarehousesStock`, así
 * que en las pantallas que usan los dos —las de armado de venta— react-query
 * sirve la misma entrada de caché y no hay un pedido de más.
 *
 * `limitWarehouses` en true: al no-owner sólo se le nombran sus locales
 * asignados, la misma regla de `WarehouseProtect` y `useGetWarehouses`.
 */
export function useWarehouseName(
  warehouseId: string | undefined
): string | undefined {
  const { items } = useGetWarehousesRest(
    [],
    true,
    ["warehouse-names"],
    false,
    false,
    true
  );

  // Se serializa para que el memo no dependa de la identidad del array, que
  // cambia en cada render de la query.
  const namesKey = (items || []).map((w: any) => `${w.id}:${w.name}`).join("|");

  return useMemo(() => {
    if (!warehouseId) return undefined;

    for (const pair of namesKey.split("|")) {
      if (!pair) continue;
      const at = pair.indexOf(":");
      if (pair.slice(0, at) === warehouseId) {
        return pair.slice(at + 1) || undefined;
      }
    }

    return undefined;
  }, [namesKey, warehouseId]);
}

export default useWarehouseName;
